import { reactive } from 'vue';
import { createAuth0Client } from '@auth0/auth0-spa-js';
import { setPersistedProvider, clearPersistedProvider } from './provider';
import { resetCookie } from '@/utils/cookies';
import { isSuccessfulAuth0Callback, parseAuthUrlError } from '@/utils/auth/authOutcome';

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () => {
    window.history.replaceState({}, document.title, window.location.pathname);
};

let instance;

/** Returns the current instance of the SDK */
export const getAuth0Instance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({ onRedirect = DEFAULT_REDIRECT_CALLBACK, redirectUri = window.location.origin, ...options } = {}) => {
    if (instance) return instance;

    // The 'instance' is a reactive object
    instance = reactive({
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
        /** Authenticates the user using a popup window */

        async loginWithPopup(options, config) {
            instance.popupOpen = true;

            try {
                await instance.auth0Client.loginWithPopup(options, config);
                instance.user = await instance.auth0Client.getUser();
                instance.isAuthenticated = await instance.auth0Client.isAuthenticated();
                instance.error = null;
            } catch (e) {
                instance.error = e;

                console.error(e);
            } finally {
                instance.popupOpen = false;
            }
        },
        /** Handles the callback when logging in using a redirect */
        async handleRedirectCallback() {
            instance.loading = true;
            try {
                await instance.auth0Client.handleRedirectCallback();
                instance.user = await instance.auth0Client.getUser();
                instance.isAuthenticated = true;
                instance.error = null;
            } catch (e) {
                instance.error = e;
            } finally {
                instance.loading = false;
            }
        },
        /** Authenticates the user using the redirect method */
        loginWithRedirect(o) {
            return instance.auth0Client.loginWithRedirect(o);
        },
        /** Returns all the claims present in the ID token */
        getIdTokenClaims(o) {
            return instance.auth0Client.getIdTokenClaims(o);
        },
        /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
        async getTokenSilently(o) {
            try {
                const token = await instance.auth0Client.getTokenSilently(o);
                instance.error = null;
                return token;
            } catch (e) {
                instance.error = e;
                throw e;
            }
        },
        /** Gets the access token using a popup window */

        getTokenWithPopup(o) {
            return instance.auth0Client.getTokenWithPopup(o);
        },
        /** Logs the user out and removes their session on the authorization server */
        logout(o) {
            clearPersistedProvider();
            resetCookie('careers-app-mode');
            return instance.auth0Client.logout(o);
        },
        clearError() {
            instance.error = null;
        },
        /** Sends a password reset email to the specified email address */
        async sendPasswordResetEmail(email) {
            const { domain, clientId } = options;
            try {
                // 2026-01-28: We reach out to this endpoint to trigger its password reset functionality.
                // https://auth0.com/docs/authenticate/database-connections/password-change#trigger-an-interactive-password-reset-flow
                const response = await fetch(`https://${domain}/dbconnections/change_password`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        client_id: clientId,
                        email,
                        connection: 'Username-Password-Authentication',
                    }),
                });

                const data = await response.text();

                if (response.ok) {
                    return { success: true, message: data };
                }
                return { success: false, error: data };
            } catch (error) {
                return { success: false, error: error.message };
            }
        },
    });

    // Initialize the auth0Client (replaces created() lifecycle hook)
    (async () => {
        // Create a new instance of the SDK client using members of the given options object
        instance.auth0Client = await createAuth0Client({
            ...options,
            authorizationParams: {
                redirect_uri: redirectUri,
                ...options.authorizationParams,
            },
        });

        try {
            // If Auth0 responds with an error, we must handle it and routeGuard
            // should handle what do do next.
            // E.g. Redirect to a password expiry page, notify user they are banned, etc.
            const authUrlError = parseAuthUrlError(window.location.search);
            if (authUrlError) {
                if (authUrlError.isPasswordExpired) {
                    window.history.replaceState({}, document.title, window.location.pathname);
                }

                instance.error = {
                    error: authUrlError.error,
                    error_description: authUrlError.errorDescription,
                };
            }

            // If the user is returning to the app after authentication..
            if (isSuccessfulAuth0Callback(window.location.search)) {
                // handle the redirect and retrieve tokens
                const { appState } = await instance.auth0Client.handleRedirectCallback();
                instance.error = null;

                // Notify subscribers that the redirect callback has happened, passing the appState
                // (useful for retrieving any pre-authentication state)
                onRedirect(appState);
            }
        } catch (e) {
            instance.error = e;
        } finally {
            // Initialize our internal authentication state
            instance.isAuthenticated = await instance.auth0Client.isAuthenticated();
            instance.user = await instance.auth0Client.getUser();

            if (instance.isAuthenticated) {
                try {
                    await instance.getTokenSilently();
                    setPersistedProvider('auth0');
                } catch {
                    // If the user has an instance.error, its likely that we should just log
                    // them out and allow them to try again. Otherwise, user will struggle to log
                    // out.
                    await instance.logout({ openUrl: false }).catch(() => {});
                    instance.isAuthenticated = false;
                    instance.user = {};
                    setPersistedProvider('');
                }
            }

            instance.loading = false;
        }
    })();

    return instance;
};
